import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Modal } from "./Modal";
import { api } from "../api/client";
import type { QuerySnapshot } from "./SelectionContext";

interface AddToPlaylistModalProps {
  open: boolean;
  onClose: () => void;
  mediaIds: number[];
  query?: QuerySnapshot;
  matched?: number;
}

export function AddToPlaylistModal({
  open,
  onClose,
  mediaIds,
  query,
  matched,
}: AddToPlaylistModalProps) {
  const qc = useQueryClient();
  const playlists = useQuery({ queryKey: ["playlists"], queryFn: api.listPlaylists, enabled: open });
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");

  function appendToPlaylist(playlistId: number): Promise<unknown> {
    if (query) {
      return api.appendPlaylistByQuery(playlistId, {
        kind: query.kind,
        tag: query.tag,
        structured: query.structured,
      });
    }
    return api.addToPlaylist(playlistId, mediaIds);
  }

  const addToExisting = useMutation({
    mutationFn: ({ playlistId }: { playlistId: number }) => appendToPlaylist(playlistId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["playlists"] });
      onClose();
    },
  });

  const createAndAdd = useMutation({
    mutationFn: async () => {
      const created = await api.createPlaylist({ name: newName.trim() });
      await appendToPlaylist(created.id);
      return created;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["playlists"] });
      setCreating(false);
      setNewName("");
      onClose();
    },
  });

  const headerCount = query ? (matched ?? 0) : mediaIds.length;
  const headerLabel = query
    ? `${headerCount.toLocaleString()} matching item${headerCount === 1 ? "" : "s"}`
    : `${headerCount} item${headerCount === 1 ? "" : "s"}`;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Add ${headerLabel} to playlist`}
    >
      {playlists.isPending ? (
        <div className="text-zinc-500">Loading…</div>
      ) : playlists.isError ? (
        <div className="text-red-300">Error: {String(playlists.error)}</div>
      ) : (
        <>
          <div className="mb-4 max-h-64 overflow-y-auto rounded border border-zinc-800">
            {playlists.data.playlists.length === 0 ? (
              <div className="p-3 text-xs text-zinc-500">
                You have no playlists yet. Create one below.
              </div>
            ) : (
              <ul className="divide-y divide-zinc-800">
                {playlists.data.playlists.map((p) => (
                  <li key={p.id}>
                    <button
                      onClick={() => addToExisting.mutate({ playlistId: p.id })}
                      disabled={addToExisting.isPending}
                      className="flex w-full items-center justify-between px-3 py-2 hover:bg-zinc-800 disabled:opacity-50"
                    >
                      <span className="text-zinc-200">{p.name}</span>
                      <span className="text-xs text-zinc-500">
                        {p.item_count} item{p.item_count === 1 ? "" : "s"}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {!creating ? (
            <button
              onClick={() => setCreating(true)}
              className="w-full rounded bg-zinc-800 px-3 py-1.5 text-sm hover:bg-zinc-700"
            >
              + New playlist
            </button>
          ) : (
            <div className="flex gap-2">
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Playlist name"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newName.trim().length > 0) createAndAdd.mutate();
                }}
                className="flex-1 rounded border border-zinc-700 bg-zinc-800 px-2 py-1 focus:border-zinc-500 focus:outline-none"
              />
              <button
                onClick={() => createAndAdd.mutate()}
                disabled={newName.trim().length === 0 || createAndAdd.isPending}
                className="rounded bg-emerald-700 px-3 py-1 text-emerald-100 hover:bg-emerald-600 disabled:opacity-50"
              >
                Create
              </button>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
